import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  forwardRef,
  useImperativeHandle,
} from "react";
import {
  Button,
  withField,
  ArrayField,
  Form,
  Tooltip,
  Popover,
} from "@douyinfe/semi-ui";
import type {
  BaseFormApi as FormApi,
  FormState,
} from "@douyinfe/semi-foundation/lib/es/form/interface";
import { debounce } from "lodash";
import { chainNameValidate, sequenceLengthValidate } from "@utils/validator";
import classNames from "classnames";
import { ReactComponent as AdvancedIcon } from "@assets/icons/advanced_icon.svg";
import { ReactComponent as MinusIcon } from "@assets/icons/minus_circle.svg";
import { ReactComponent as AddIcon } from "@assets/icons/add_icon.svg";
import {
  ConstraintModel,
  SequenceRowModel,
} from "@services/home/model/home.model";
import { useTranslation } from "react-i18next";
import { getChainIds } from "@utils/data-process.util";
import styles from "./index.module.scss";

interface FormValuesModel {
  constraints: ConstraintModel[];
}

interface Prop {
  sequences?: SequenceRowModel[];
  value?: ConstraintModel[];
  isTuition: boolean;
  onChange?: (values: any) => void;
  onValidate?: (valid: boolean) => void;
}

const DockingAdvancedSettingField = forwardRef<any, Prop>((props, ref) => {
  const { t, i18n } = useTranslation();
  const { value, onChange, onValidate, sequences, isTuition } = props;

  const formRef = useRef<FormApi<FormValuesModel>>();

  const [visible, setVisible] = useState(true);

  const [sequenceMap, setSequenceMap] = useState<
    Record<string, SequenceRowModel>
  >({});

  const initialData: ConstraintModel[] = [{} as ConstraintModel];

  function onValueChange(values: FormValuesModel) {
    if (onChange) {
      onChange(values.constraints);
    }
  }

  const debouncedOnValueChange = useMemo(
    () => debounce(onValueChange, 500),
    [onValueChange],
  );

  function onClose() {
    if (!formRef.current) {
      return;
    }
    const constraints = formRef.current.getValue("constraints");

    const haveInput = constraints.some((item) => {
      return Object.keys(item).length > 0;
    });

    if (!haveInput) {
      setVisible(false);

      return;
    }

    formRef.current
      .validate()
      .then((values: FormValuesModel) => {
        setVisible(false);
        if (onValidate) {
          onValidate(true);
        }
      })
      .catch((e) => {
        if (onValidate) {
          onValidate(false);
        }
      });
  }
  function onOpen(e: React.MouseEvent<HTMLButtonElement, MouseEvent>) {
    e.stopPropagation();
    e.preventDefault();
    setVisible(true);
  }

  function setConstraints(val: ConstraintModel[] | undefined) {
    if (val) {
      const haveInput = val.some((item) => {
        return Object.keys(item).length > 0;
      });

      if (haveInput) {
        // setVisible(true);
        setTimeout(() => {
          formRef.current?.setValue("constraints", val);
        }, 100);
      }
    } else {
      formRef.current?.setValue("constraints", [{} as ConstraintModel]);
    }
  }

  useEffect(() => {
    setConstraints(value);
  }, [JSON.stringify(value)]);

  useEffect(() => {
    if (sequences) {
      const map = sequences.reduce((ac, item, i) => {
        const chianIds = getChainIds(sequences, i, item.copies);
        const chianNames = chianIds.split(",");

        chianNames.reduce((a, name) => {
          a[name] = item;

          return a;
        }, ac);

        return ac;
      }, {} as Record<string, SequenceRowModel>);

      setSequenceMap(map);
    } else {
      setSequenceMap({});
    }
  }, [JSON.stringify(sequences)]);

  useImperativeHandle(ref, () => ({
    async validate(): Promise<boolean> {
      try {
        await formRef.current!.validate();

        setVisible(false);
        return true;
      } catch (e) {
        setVisible(true);
        return false;
      }
    },

    getValues() {
      if (!formRef.current) {
        return null;
      }

      return formRef.current.getValue("constraints");
    },
    openPopover() {
      setVisible(true);
    },
  }));

  return (
    <Popover
      keepDOM
      position="leftTop"
      trigger="custom"
      visible={visible}
      onClickOutSide={onClose}
      content={
        <Form
          className={styles.formContainer}
          onValueChange={debouncedOnValueChange}
          getFormApi={(formApi) => (formRef.current = formApi)}
        >
          {({ formState }: { formState: FormState<FormValuesModel> }) => {
            const error = formState.errors;
            return (
              <ArrayField field="constraints" initValue={initialData}>
                {({ add, arrayFields, addWithInitValue }) => (
                  <React.Fragment>
                    <div className={styles.titleContainer}>
                      <div className={styles.title}>
                        {t("homePage.ab_ag_dock_lite.constraints.title")}
                      </div>
                      <div
                        className={styles.subTitle}
                        dangerouslySetInnerHTML={{
                          __html: t(
                            "homePage.ab_ag_dock_lite.constraints.text",
                          ),
                        }}
                      ></div>
                    </div>
                    <div className={styles.content}>
                      {arrayFields.map(({ field, key, remove }, i) => {
                        const constraintError = error?.constraints?.[i] as
                          | undefined
                          | Record<string, string>;

                        return (
                          <div key={key} className={styles.row}>
                            <div className={styles.boldText}>
                              {t("homePage.ab_ag_dock_lite.constraints.chain")}
                            </div>
                            <Tooltip
                              key={`${field}[chain_id_b]`}
                              content={constraintError?.chain_id_b}
                              mouseEnterDelay={
                                constraintError?.chain_id_b ? 0.1 : 99999
                              }
                              // mouseLeaveDelay={0.1}
                            >
                              <Form.Input
                                pure
                                field={`${field}[chain_id_b]`}
                                noLabel
                                size="small"
                                trigger="blur"
                                style={{ width: 40 }}
                                placeholder="A"
                                validate={(v, record) =>
                                  chainNameValidate(
                                    v as string,
                                    record,
                                    i,
                                    "chain_id_b",
                                    Object.keys(sequenceMap),
                                  )
                                }
                              ></Form.Input>
                            </Tooltip>

                            <Tooltip
                              key={`${field}[resi_ids_b]`}
                              content={constraintError?.resi_ids_b}
                              mouseEnterDelay={
                                constraintError?.resi_ids_b ? 0.1 : 99999
                              }
                              // mouseLeaveDelay={0.1}
                            >
                              <Form.Input
                                pure
                                trigger="blur"
                                size="small"
                                field={`${field}[resi_ids_b]`}
                                noLabel
                                style={{ width: 210 }}
                                placeholder="540,587-589"
                                validate={(v, record) =>
                                  sequenceLengthValidate(
                                    v as string,
                                    sequenceMap,
                                    (record as FormValuesModel).constraints[i]
                                      .chain_id_b,
                                  )
                                }
                              ></Form.Input>
                            </Tooltip>

                            <div className={styles.boldText}>
                              {t(
                                "homePage.ab_ag_dock_lite.constraints.toChain",
                              )}
                            </div>
                            <Tooltip
                              key={`${field}[chain_id_a]`}
                              content={constraintError?.chain_id_a}
                              mouseEnterDelay={
                                constraintError?.chain_id_a ? 50 : 99999
                              }
                              // mouseLeaveDelay={0.1}
                            >
                              <Form.Input
                                pure
                                trigger="blur"
                                placeholder="H"
                                field={`${field}[chain_id_a]`}
                                noLabel
                                size="small"
                                style={{ width: 40 }}
                                validate={(v, record) =>
                                  chainNameValidate(
                                    v as string,
                                    record,
                                    i,
                                    "chain_id_a",
                                    Object.keys(sequenceMap),
                                  )
                                }
                              ></Form.Input>
                            </Tooltip>

                            <Button
                              className={styles.lightText}
                              theme="borderless"
                              type="tertiary"
                              size="small"
                              icon={<MinusIcon />}
                              onClick={remove}
                            />
                          </div>
                        );
                      })}
                    </div>

                    <div className={styles.footer}>
                      <Button
                        onClick={add}
                        type="tertiary"
                        icon={<AddIcon />}
                        theme="borderless"
                        className={styles.btn}
                      ></Button>

                      {arrayFields.length === 0 && (
                        <span className={styles.addText}>
                          {t("homePage.ab_ag_dock_lite.constraints.addText")}
                        </span>
                      )}
                    </div>
                  </React.Fragment>
                )}
              </ArrayField>
            );
          }}
        </Form>
      }
    >
      <div>
        <Tooltip content={t("homePage.ab_ag_dock_lite.constraints.tooltip")}>
          <Button
            disabled={isTuition}
            className={classNames({
              [styles.advancedBtn]: true,
              [styles.active]: visible,
            })}
            id="docking-constraints-button"
            theme="borderless"
            type="tertiary"
            size="small"
            onClick={onOpen}
            icon={<AdvancedIcon />}
          ></Button>
        </Tooltip>
      </div>
    </Popover>
  );
});

DockingAdvancedSettingField.displayName = "DockingAdvancedSettingField";

export default withField(DockingAdvancedSettingField);
